import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import styled from 'styled-components';
import {useFormikContext} from 'formik';
import excel from 'shared/Excel';
import {Icon} from 'shared/helpers';
import {GenericFields} from 'components/customer/Product/Specs/GenericFields';
import {COMPACT_PAD} from 'components/customer/Product/Specs/DrawerFieldsRow';
import {useCompactLayout} from 'components/customer/Product/Drawer/helpers/useCompactLayout';
import {Fieldset} from 'components/customer/Product/entity/Fieldset';
import {Field} from 'components/customer/Product/entity/Field';
import {toNumber} from 'components/customer/Product/Drawer/helpers';

interface CollapsibleFieldsetProps {
    fieldset: Fieldset;
    hasPreview?: boolean;
    aside?: React.ReactNode;
    // lead in for a boolean summary: "Edged: Width 1, Length 1"
    enabledPrefix?: string;
    // one more entry after the sizes, for a field they cannot summarise
    summaryExtra?: string;
    // opens the group when this has a value, at mount or whenever it changes
    // to one, so a change made to its fields from elsewhere is seen, and
    // closes it again when it empties, unless the customer has opened or
    // closed it since
    expandOn?: unknown;
}

interface FieldsetValues {
    [key: string]: unknown;
}

const formatMm = (value: number) => String(Math.round(value * 10) / 10);

const isOn = (value: unknown) => value === true || toNumber(value) > 0;

// only skips outright hidden fields, Field owns formula visibility
const isHidden = (field: Field) => field.options?.visible === false;

// One collapsible row. The body still renders from the fieldset structure.
export const CollapsibleFieldset = ({
    fieldset,
    hasPreview = false,
    aside = null,
    enabledPrefix,
    summaryExtra,
    expandOn,
}: CollapsibleFieldsetProps) => {
    const {values} = useFormikContext<FieldsetValues>();

    // collapsed by default, honours options.collapsed if the structure sets it
    const [isOpen, setIsOpen] = useState(() => {
        if (expandOn) return true;

        const collapsed = fieldset.options?.collapsed;

        if (typeof collapsed === 'boolean') return !collapsed;

        if (typeof collapsed === 'string') {
            try {
                return !excel.calculate(collapsed, values);
            } catch (error) {
                return false;
            }
        }

        return false;
    });

    const compact = useCompactLayout();

    // Mounting the body sets its dropdowns fetching their options, so a body
    // that is thrown away on collapse comes back empty for a moment every
    // time. Once opened it stays mounted and hides instead.
    const [wasOpened, setWasOpened] = useState(isOpen);

    const expandSeen = useRef(expandOn);
    const openedByExpand = useRef(Boolean(expandOn));

    const toggleHandler = useCallback(() => {
        setIsOpen((isOpen) => !isOpen);
        setWasOpened(true);
        openedByExpand.current = false;
    }, []);

    useEffect(() => {
        if (expandOn === expandSeen.current) return;

        expandSeen.current = expandOn;

        if (expandOn) {
            if (!isOpen) {
                openedByExpand.current = true;
                setIsOpen(true);
                setWasOpened(true);
            }
        } else if (openedByExpand.current) {
            openedByExpand.current = false;
            setIsOpen(false);
        }
    }, [expandOn, isOpen]);

    // sizes list every value with its unit, booleans name what is switched on
    const summary = useMemo(() => {
        const fields = fieldset.fields.filter((field) => !isHidden(field));

        const whole = (text: string) => text.replace(/ /g, '\u00a0');

        // folded, the summary wraps between entries, never inside one
        const entry = (text: string) => (compact ? whole(text) : text);

        const sizes = fields
            .filter((field) => field.type === 'size')
            .map(
                (field) =>
                    `${field.displayName} ${formatMm(
                        toNumber(values[String(field.name)])
                    )}mm`
            );

        // the extra entry reads as one phrase, so it is never split
        if (sizes.length) {
            return [
                ...sizes.map(entry),
                ...(summaryExtra ? [whole(summaryExtra)] : []),
            ].join(' · ');
        }

        const booleans = fields.filter((field) => field.type === 'boolean');

        if (booleans.length) {
            const on = booleans
                .filter((field) => isOn(values[String(field.name)]))
                .map((field) => entry(field.displayName));
            const list = on.length ? on.join(', ') : 'None';

            return enabledPrefix ? `${enabledPrefix}: ${list}` : list;
        }

        return '';
    }, [fieldset, values, enabledPrefix, summaryExtra, compact]);

    return (
        <Section>
            <Header
                type="button"
                $compact={compact}
                onClick={toggleHandler}
                aria-expanded={isOpen}>
                <Caret $open={isOpen}>
                    <Icon iconName="Chevron-down.svg" alt="" />
                </Caret>
                <Title>{fieldset.title}</Title>
                {isOpen ? null : (
                    <Summary $compact={compact}>{summary}</Summary>
                )}
                {compact ? null : <Spacer />}
                {aside === null ? null : (
                    <Aside $compact={compact}>{aside}</Aside>
                )}
            </Header>

            {wasOpened ? (
                <Body $compact={compact} hidden={!isOpen}>
                    <GenericFields
                        fields={fieldset.fields}
                        fieldsetName={fieldset.name}
                        fieldsetLayout={fieldset.options?.layout}
                        fieldsetTitle={false}
                        preview={hasPreview}
                        stackFields
                    />
                </Body>
            ) : null}
        </Section>
    );
};

const Section = styled.section`
    border-bottom: 1px solid #e6eef7;

    &:last-child {
        border-bottom: none;
    }
`;

// folded, a grid of caret, title and aside, with the summary on a second row
const Header = styled.button<{$compact: boolean}>`
    width: 100%;
    display: ${({$compact}) => ($compact ? 'grid' : 'flex')};
    grid-template-columns: 12px max-content 1fr;
    align-items: center;
    gap: ${({$compact}) => ($compact ? '6px 10px' : '10px')};
    padding: 11px ${({$compact}) => ($compact ? COMPACT_PAD : 18)}px;
    background: none;
    border: none;
    font: inherit;
    text-align: left;
    cursor: pointer;

    &:hover {
        background: #f6faff;
    }
`;

const Caret = styled.span<{$open: boolean}>`
    display: inline-flex;
    flex: none;
    align-items: center;
    justify-content: center;
    width: 12px;
    height: 12px;
    transform: rotate(${({$open}) => ($open ? 0 : -90)}deg);
    transition: transform 0.15s ease;

    img {
        width: 100%;
    }
`;

const Title = styled.span`
    font-weight: 700;
    color: ${({theme}) => theme.colors.primary?.main};
    white-space: nowrap;
    font-size: 0.85rem;
`;

const Muted = styled.span<{$compact: boolean}>`
    color: #6b7480;
    font-size: 12px;
    font-variant-numeric: tabular-nums;
`;

// folded, the full width under the title and any aside; inline-size
// containment stops it widening the title's column
const Summary = styled(Muted)`
    grid-row: 2;
    grid-column: 2 / -1;
    contain: ${({$compact}) => ($compact ? 'inline-size' : 'none')};
`;

const Aside = styled(Muted)`
    grid-row: 1;
    grid-column: 3;
    text-align: ${({$compact}) => ($compact ? 'right' : 'left')};
    text-wrap: ${({$compact}) => ($compact ? 'balance' : 'wrap')};
`;

const Spacer = styled.span`
    flex: 1;
`;

// folded, as many equal columns as fit, so a phone gets two
const Body = styled.div<{$compact: boolean}>`
    display: ${({$compact}) => ($compact ? 'grid' : 'flex')};
    grid-template-columns: repeat(auto-fill, minmax(115px, 1fr));
    flex-wrap: wrap;
    /* folded, the caret indent costs 20px the fields need; at 36px the fifth
       margin wraps even at the card's 777px widest */
    gap: 2px ${({$compact}) => ($compact ? 16 : 34)}px;
    padding: ${({$compact}) =>
        $compact
            ? `4px ${COMPACT_PAD}px 14px ${COMPACT_PAD}px`
            : '4px 18px 14px 40px'};

    /* the attribute on its own cannot hide a flex box */
    &[hidden] {
        display: none;
    }

    /* GenericFields nests the Row deeper than a child selector reaches, so
       bootstrap's -12px margin was pulling the fields out of the card */
    .row {
        margin-left: 0;
        margin-right: 0;
    }

    [class*='col-'] {
        padding-left: 0;
        padding-right: 0;
    }

    /* FormGroup indents 10px, which would set these in from the row below */
    [class*='col-'] > div {
        padding-left: 0;
    }

    /* Field wraps each field in its own full width <Row>, so pack those */
    > .row {
        flex: 0 0 auto;
        width: ${({$compact}) => ($compact ? 'auto' : '115px')};
        margin: 0;
        display: block;
    }

    > .row > [class*='col-'] {
        flex: none;
        width: 100%;
        max-width: 100%;
    }

    /* bootstrap gutters the control column but not the label, so they misalign */
    > .row [class*='col-'] {
        padding-left: 0;
        padding-right: 0;
    }

    /* FormGroup indents 10px, pushing fields out of line with the heading */
    > .row > [class*='col-'] > div {
        padding-left: 0;
        margin-bottom: 4px;
    }

    /* pinned absolute top right by default, keep it beside the label text */
    label .more-info {
        position: relative !important;
        top: auto;
        right: auto;
        display: inline-flex;
        vertical-align: middle;
        margin-left: 4px;
    }
`;
